import { SHAPE_PART_ID, SQFT_FEATURES } from "../../../sqft/constants";
import { getFeatureGroupName, getFeatureUnit } from "../../../../misc/helpers";
import store from "../../../../store";
import { getCommonVisualizerType } from "../../../../config";

export const addOversizedFeatureIfNeeded = (
  sqftFeatures,
  sqftPassedData,
  layout,
  intl,
  fabricatorData,
  quoteIndex,
  visualizerType
) => {
  const oversizedSettings = getOversizedSqftSettings(visualizerType);
  if (!oversizedSettings.enableOversizedSqftFeature) {
    return sqftFeatures;
  }

  if (!hasValidOversizedSqftSettings(oversizedSettings) || !isOversizedFeatureNeeded(sqftPassedData, oversizedSettings.oversizedSqftThreshold)) {
    return sqftFeatures;
  }

  const oversizedFeature = findOversizedFeature(layout, oversizedSettings.oversizedSqftFeatureId);
  if (!oversizedFeature || !oversizedFeature.getParent) {
    console.warn("Oversized feature not found. Id:", oversizedSettings.oversizedSqftFeatureId);
    return sqftFeatures;
  }

  const featureGroup = oversizedFeature.getParent();
  const sqftId = getOversizedSqftId(quoteIndex);
  const subPart = {
    part: featureGroup,
    text: getFeatureGroupName(featureGroup, intl, fabricatorData),
  };

  sqftFeatures[SQFT_FEATURES.oversized] = {
    subPart,
    features: [{
      disabled: false,
      isDisabledInPopup: false,
      feature: oversizedFeature,
      partId: featureGroup.partId,
      unit: getFeatureUnit(oversizedFeature, intl, fabricatorData, 1),
      subPart,
      quantity: 1,
      quantityRounded: 1,
      displayFakeQuantity: null,
      displayFakePrice: null,
      initialQuantity: 1,
      sqftId,
      sqftIds: [sqftId],
      sqftQuantities: {[sqftId]: 1},
      qtCatName: sqftPassedData.qtCatName,
      shouldShowEyeBall: false,
    }]
  };

  return sqftFeatures;
};

export const getOversizedFeatureGroup = (sqftPassedData, layout, visualizerType) => {
  const oversizedSettings = getOversizedSqftSettings(visualizerType);
  if (!oversizedSettings.enableOversizedSqftFeature) {
    return null;
  }

  if (!hasValidOversizedSqftSettings(oversizedSettings) || !isOversizedFeatureNeeded(sqftPassedData, oversizedSettings.oversizedSqftThreshold)) {
    return null;
  }

  const oversizedFeature = findOversizedFeature(layout, oversizedSettings.oversizedSqftFeatureId);
  return oversizedFeature && oversizedFeature.getParent ? oversizedFeature.getParent() : null;
};

const getOversizedSqftSettings = (visualizerType) => {
  const {fabricatorState} = store.getState();
  const settingsKey = `${getCommonVisualizerType(visualizerType)}Settings`;
  const settings = fabricatorState[settingsKey] || fabricatorState.settings || {};

  return {
    enableOversizedSqftFeature: !!settings.enableOversizedSqftFeature,
    oversizedSqftFeatureId: settings.oversizedSqftFeatureId,
    oversizedSqftThreshold: Number.isFinite(+settings.oversizedSqftThreshold)
      ? +settings.oversizedSqftThreshold
      : null,
  };
};

const hasValidOversizedSqftSettings = (oversizedSettings) => {
  return Number.isFinite(+oversizedSettings.oversizedSqftFeatureId)
    && Number.isFinite(+oversizedSettings.oversizedSqftThreshold);
};

const isOversizedFeatureNeeded = (sqftPassedData, oversizedSqftThreshold) => {
  return (getCountertopArea(sqftPassedData) + getBacksplashArea(sqftPassedData)) > oversizedSqftThreshold
};

const getCountertopArea = (sqftPassedData) => {
  if (!sqftPassedData || !Array.isArray(sqftPassedData.countertops)) {
    return 0;
  }

  return sqftPassedData.countertops.reduce((total, item) => {
    if (!item || !["countertop", "center_island_countertop"].includes(item.partId)) {
      return total;
    }
    if (!sqftPassedData.manualCalculate && (!item.shapeType || ["backsplash", SHAPE_PART_ID.seamPiece, SHAPE_PART_ID.slabPlaceHolder].includes(item.type))) {
      return total;
    }
    return total + getAreaValue(item);
  }, 0);
};

const getBacksplashArea = (sqftPassedData) => {
  if (!sqftPassedData) {
    return 0;
  }

  const fromCountertops = Array.isArray(sqftPassedData.countertops)
    ? sqftPassedData.countertops
      .filter(item => item && (item.type === "backsplash" || item.partId === "backsplash"))
      .reduce((total, item) => total + getAreaValue(item), 0)
    : 0;

  const fromManualBacksplashes = Array.isArray(sqftPassedData.backsplashes)
    ? sqftPassedData.backsplashes.reduce((total, item) => total + getAreaValue(item), 0)
    : 0;

  return fromCountertops + fromManualBacksplashes;
};

const getAreaValue = (item) => {
  const value = item.squareFeet || item.area || item.quantity || item.length || 0;
  return Number.isFinite(+value) ? +value : 0;
};

const getOversizedSqftId = (quoteIndex) => `sqft-oversized-${quoteIndex}`;

const findOversizedFeature = (layout, oversizedSqftFeatureId) => {
  if (!layout || !layout.getAllFeatures) {
    return null;
  }

  const featureId = +oversizedSqftFeatureId;
  if (!Number.isFinite(featureId)) {
    return null;
  }

  return layout.getAllFeatures(true).find(feature => {
    const dbFeature = feature && feature.dbFeature ? feature.dbFeature : {};
    return dbFeature.featureDefinitionID === featureId;
  });
};
